import React, {useCallback, useEffect, useState} from 'react'
import {Box, CircularProgress} from '@mui/material'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import moment from 'moment-timezone'
import Card from 'src/components-new/layout/Card'
import TabBar from 'src/components-new/layout/TabBar'
import EmptyState from 'src/components-new/layout/EmptyState'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'

interface GraphsTabProps {
  patientId: string
}

interface GraphSeries {
  name: string
  data: (number | null)[]
  type: 'line'
}

interface GraphItem {
  key: string
  label: string
  title: string
  xAxis: string[]
  series: GraphSeries[]
}

const FILTER_CONFIG: FilterItem[] = [
  {id: 'date', label: 'Data', type: 'rangePicker'},
]

function processGraphData(data: any[]): Record<string, GraphItem[]> {
  if (!Array.isArray(data)) return {}

  const processed: Record<string, GraphItem[]> = {}

  data.forEach((questionnaire: any, index: number) => {
    const {title, formSchemaId, questionnaireWithScores} = questionnaire
    if (!questionnaireWithScores || questionnaireWithScores.length === 0) return

    const groupKey = `${title}-${formSchemaId}-${index}`
    processed[groupKey] = []

    const scoreKeys: Record<string, string> = {}
    questionnaireWithScores.forEach((submission: any) => {
      Object.keys(submission.scores).forEach(key => {
        if (!scoreKeys[key]) {
          scoreKeys[key] = submission.scores[key].label
        }
      })
    })

    const xAxis = questionnaireWithScores.map((s: any) => moment(s.date).tz('Europe/Rome').format('DD/MM/YYYY'))

    Object.keys(scoreKeys).forEach(scoreKey => {
      const seriesData = questionnaireWithScores.map((s: any) => {
        const score = s.scores[scoreKey]
        return score && score.value ? parseFloat(score.value) : null
      })

      processed[groupKey].push({
        key: scoreKey,
        label: scoreKeys[scoreKey],
        title,
        xAxis,
        series: [{
          name: scoreKeys[scoreKey],
          data: seriesData,
          type: 'line',
        }],
      })
    })
  })

  return processed
}

function GraphChart({graph}: {graph: GraphItem}): JSX.Element {
  const options: Highcharts.Options = {
    chart: {type: 'line'},
    title: {text: undefined},
    legend: {enabled: false},
    xAxis: {categories: graph.xAxis},
    yAxis: {min: 0, title: {text: undefined}},
    colors: ['#4472C4'],
    tooltip: {pointFormat: '{series.name}: <b>{point.y:.2f}</b>'},
    plotOptions: {
      series: {
        animation: false,
        marker: {symbol: 'circle', enabled: true, radius: 3, lineWidth: 2},
        dataLabels: {enabled: true, format: '{point.y:.2f}'},
      },
    },
    credits: {enabled: false},
    series: graph.series,
  }

  return <HighchartsReact highcharts={Highcharts} options={options} />
}

export default function GraphsTab({patientId}: GraphsTabProps): JSX.Element {
  const [graphData, setGraphData] = useState<Record<string, GraphItem[]>>({})
  const [tabValues, setTabValues] = useState<Record<string, string>>({})
  const [filters, setFilters] = useState<FilterResult[]>([])
  const [status, setStatus] = useState<string>(STATUS.PENDING)

  const fetchData = useCallback(() => {
    apiClient.getPatientQuestionnaireGraphs(patientId, filters, setStatus)
      .then((data: any) => {
        const processed = processGraphData(data ?? [])
        setGraphData(processed)

        const initialTabValues: Record<string, string> = {}
        Object.keys(processed).forEach(groupKey => {
          if (processed[groupKey]?.length > 0) {
            initialTabValues[groupKey] = processed[groupKey][0].key
          }
        })
        setTabValues(initialTabValues)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId, filters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setFilters(values)
  }

  const handleTabChange = (groupKey: string, key: string) => {
    setTabValues(prev => ({...prev, [groupKey]: key}))
  }

  return (
    <Box>
      {status === STATUS.REJECTED && (
        <InlineMessage
          severity="error"
          onClose={() => setStatus(STATUS.FULFILLED)}
          sx={{mb: 2}}
        >
          {'Errore nel caricare i questionari.'}
        </InlineMessage>
      )}

      <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
        <Filters config={FILTER_CONFIG} onSubmit={handleFiltersSubmit} />
      </Box>

      {status === STATUS.PENDING ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          {Object.entries(graphData).map(([groupKey, graphItems]) => {
            if (!Array.isArray(graphItems) || graphItems.length === 0) return null

            const selectedKey = tabValues[groupKey] ?? graphItems[0].key
            const selectedGraph = graphItems.find(graph => graph.key === selectedKey)
            const title = graphItems[0]?.title

            return (
              <Card key={groupKey} title={title}>
                <TabBar
                  tabs={graphItems.map(graph => ({key: graph.key, label: graph.label}))}
                  activeKey={selectedKey}
                  onChange={key => handleTabChange(groupKey, key)}
                />
                {selectedGraph ? (
                  <Box sx={{pt: 2}}>
                    <GraphChart graph={selectedGraph} />
                  </Box>
                ) : (
                  <EmptyState
                    title="Nessun dato disponibile"
                    description="Non ci sono statistiche per questo grafico."
                  />
                )}
              </Card>
            )
          })}
        </Box>
      )}
    </Box>
  )
}
